import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Business Modelling) ---
import {
    // Core Concepts & General
    faProjectDiagram,   // Business Modelling / Architecture / Framework
    faLightbulb,        // Innovation / Vision / Strategy
    faChartLine,        // Growth / Profitability / KPIs / Results
    faTasks,            // Efficiency / Operations / Value Prop
    faUsers,            // Partnerships / Patient Satisfaction / Community
    faCheckCircle,      // Compliance / Quality / Success
    faBuilding,         // Facility / Enterprise
    // 10 Strategic Points Icons
    faMapSigns,         // 1. Market Positioning
    faBullseye,         // 2. Business Vision
    faSitemap,          // 3. Multi-Channel Revenue Stream (Tree structure)
    // faProjectDiagram reused for 4. Operational Architecture
    faCalculator,       // 5. Financial Structuring
    faLaptopCode,       // 6. Digital & Technology Blueprint
    faGavel,            // 7. Legal, Regulatory & Quality Compliance
    faHandHoldingDollar,// 8. Investment, Partnership & Funding
    faUsersCog,         // 9. Human Resource Structuring
    faExpandArrowsAlt,  // 10. Scalability, Expansion & Future-Proofing
    // Real Impact Icons
    faTrophy,           // Success / Turnaround
    faThumbsUp,         // Satisfaction / Positive Results
    // Value Prop Icons (reuse some from above)
    // faTasks, faDollarSign, faUsers (trust), faLightbulb, faMedal (compete)
    faDollarSign,
    faMedal,
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faLaptopMedical, faNetworkWired, faDatabase, faShieldAlt, faHeartPulse, faWrench,
    faCogs, faChalkboardTeacher, faExchangeAlt, faPlayCircle, faLifeRing, faFileInvoiceDollar,
    faUserCheck, faClipboardCheck, faCashRegister, faBoxesStacked, faWarehouse, faClipboardList,
    faBolt, faSearch, faBalanceScale, faChartBar, faCalendarAlt, faShoppingCart
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Critical?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // 10-Step Framework
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Real Impact
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor Value / Path to Success

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for business strategy/modelling
    const heroBgImage = 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing business planning/collaboration

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Business Modelling for Healthcare</h1>
            <p className="cliniqor-sub-heading">
              Step into the Future with Cliniqor — Your Strategic Partner in Healthcare Innovation and Sustainability
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor offers end-to-end expertise in crafting dynamic, customized, and scalable business models tailored to modern healthcare challenges, turning vision into strategy and strategy into measurable success.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY BUSINESS MODELLING IS CRITICAL --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why Business Modelling is Critical for Healthcare Institutions</h2>
              <p>A roadmap beyond treatment and diagnosis towards sustainability.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Healthcare now includes value-based care, patient-centric services, tech-enabled delivery, and financial sustainability. A robust business model helps leaders define long-term goals, identify scalable revenue streams, optimize operations, minimize risks, respond swiftly to market changes, and build a sustainable ecosystem with predictable revenue and measurable KPIs.</p>
                     <p>Cliniqor provides a future-ready framework to reimagine service delivery, technology integration, and revenue strategies under one coherent vision.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
                 {/* Optional: Can add a small icon list of benefits here */}
             </div>
          </div>
        </section>

        {/* --- 10-STEP FRAMEWORK (Detailed Planning Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Cliniqor’s Comprehensive 10-Step Strategic Business Modelling Framework</h2>
               <p>A structured approach from market positioning to future-proofing.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 10 Steps */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapSigns} /></div>
                    <div className="cliniqor-content"><h4>1. Market Positioning & Competitive Benchmarking</h4> <p>Deep market research to evaluate trends, competitors, demographics for strategic positioning.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullseye} /></div>
                    <div className="cliniqor-content"><h4>2. Business Vision & Strategic Alignment</h4> <p>Translating mission, vision, and care philosophy into structured business objectives.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div>
                    <div className="cliniqor-content"><h4>3. Multi-Channel Revenue Stream Design</h4> <p>Identifying diversified opportunities (diagnostics, pharmacy, tech, tourism, partnerships).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <div className="cliniqor-content"><h4>4. Operational Architecture Development</h4> <p>Designing optimized operating models (centralized/decentralized), SOPs, workflows.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCalculator} /></div>
                    <div className="cliniqor-content"><h4>5. Financial Structuring & Cost Optimization</h4> <p>Detailed financial planning (CAPEX, OPEX, P&L, ROI) to increase margins and reduce waste.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <div className="cliniqor-content"><h4>6. Digital & Technology Blueprint</h4> <p>Designing and implementing intelligent digital ecosystems (HIS, EMR, AI, Telehealth).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faGavel} /></div>
                    <div className="cliniqor-content"><h4>7. Legal, Regulatory & Quality Compliance</h4> <p>Ensuring adherence to  JCI, HIPAA, environmental norms, safety, licensing.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHandHoldingDollar} /></div>
                    <div className="cliniqor-content"><h4>8. Investment, Partnership & Funding Strategy</h4> <p>Attracting investors/partners (PPP, PE, VC) with a bankable business model.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <div className="cliniqor-content"><h4>9. Human Resource Structuring & Performance KPIs</h4> <p>Defining hierarchy, roles, training, ratios, and indicators for a skilled workforce.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faExpandArrowsAlt} /></div>
                    <div className="cliniqor-content"><h4>10. Scalability, Expansion & Future-Proofing</h4> <p>Designing models supporting future growth, new departments, tech adoption, and market changes.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- REAL IMPACT (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Real Impact: Transforming Vision into Tangible Results</h2>
                 <p>Empowering healthcare organizations across India and abroad.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Impact Points --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBuilding} /></div> {/* Greenfield */}
                            <h4>Successful Greenfield Launches</h4>
                            <p>Minimized risk and maximized cost-efficiency for new projects.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTrophy} /></div>
                            <h4>Distressed Hospital Turnarounds</h4>
                            <p>Revamped operational models for sustainable recovery.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                            <h4>Improved EBITDA</h4>
                            <p>Achieved up to 40% improvement within 12–18 months.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faThumbsUp} /></div>
                            <h4>Enhanced Patient Satisfaction</h4>
                            <p>Integrated patient-centric models with advanced technology.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCheckCircle} /></div>
                            <h4>Regulatory Readiness</h4>
                            <p>Ensured faster accreditations (JCI).</p>
                        </div>
                    </div>
                     {/* Add an empty card or adjust grid CSS if needed for alignment */}
                    {/* <div className="cliniqor-feature-card cliniqor-fade-in-up" style={{ visibility: 'hidden' }}></div> */}
               </div>
               <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '20px' }}>
                   <p>Our success lies in our ability to blend strategic thinking with deep clinical insight and operational excellence.</p>
              </div>
           </div>
        </section>

        {/* --- CLINIQOR VALUE / PATH TO SUCCESS (Features Layout - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section"> {/* Default Background */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Let Cliniqor Design Your Path to Healthcare Success</h2>
                 <p>A mission-critical strategy for thriving in a dynamic environment.</p>
              </div>
               <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginBottom: '30px' }}>
                   <p>With our expert consultants, proprietary tools, and research-based models, we help you:</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Value Points --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div> {/* Efficiency */}
                            <h4>Minimize Inefficiencies</h4>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <h4>Boost Profitability</h4>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div> {/* Trust */}
                            <h4>Enhance Patient Trust</h4>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div>
                            <h4>Embrace Innovation</h4>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMedal} /></div>
                            <h4>Outperform Competition</h4>
                        </div>
                    </div>
               </div>
                <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '30px' }}>
                   <p>Partner with Cliniqor and transform your healthcare organization into a high-performance institution delivering exceptional value to patients, investors, and the community.</p>
              </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Shape the Future of Healthcare—Strategically, Sustainably, Successfully</h3>
          <p>Ready to develop a robust business model for your healthcare facility? Partner with Cliniqor to navigate challenges and unlock opportunities.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Business Modelling Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;